import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { Review } from '../../types';
import DashboardHeader from '../dashboard/DashboardHeader';
import Footer from '../dashboard/Footer';
import { formatPhoneNumber } from '../../utils/phoneFormatter';
import './ReviewDetail.css';

const ReviewDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [review, setReview] = useState<Review | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showOnSite, setShowOnSite] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  useEffect(() => {
    fetchReview();
  }, [id]);

  const fetchReview = async () => {
    try {
      setLoading(true);
      const response = await api.get<Review>(`/reviews/${id}`);
      setReview(response.data);
      setShowOnSite(response.data.show_on_site || false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load review');
    } finally {
      setLoading(false);
    }
  };

  const handleShowOnSiteToggle = async () => {
    const newValue = !showOnSite;
    try {
      setUpdating(true);
      await api.patch(`/reviews/${id}/show-on-site`, { show_on_site: newValue });
      setShowOnSite(newValue);
      if (review) {
        setReview({ ...review, show_on_site: newValue });
      }
    } catch (err: any) {
      console.error('Failed to update show_on_site:', err);
      alert(err.response?.data?.message || 'Failed to update show on site status');
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      await api.patch(`/reviews/${id}/status`, { status: 'approved' });
      if (review) {
        setReview({ ...review, status: 'approved' });
      }
    } catch (err: any) {
      console.error('Failed to approve review:', err);
      alert(err.response?.data?.message || 'Failed to approve review');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await api.delete(`/reviews/${id}`);
      alert('Review deleted successfully');
      navigate('/dashboard?tab=reviews');
    } catch (err: any) {
      console.error('Failed to delete review:', err);
      alert(err.response?.data?.message || 'Failed to delete review');
    } finally {
      setDeleting(false);
    }
  };

  const copyText = async (text: string): Promise<boolean> => {
    try {
      // Clipboard API requires a secure context; fall back to execCommand for non-secure builds.
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (err) {
      // Fall back to textarea copy
      console.warn('Clipboard API failed, falling back to execCommand:', err);
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-999999px';
      textarea.style.top = '-999999px';

      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);
      return successful;
    } catch (err) {
      console.error('execCommand copy failed:', err);
      return false;
    }
  };

  const copyToClipboard = async (text: string, type: 'email' | 'phone') => {
    const didCopy = await copyText(text);
    if (!didCopy) {
      console.error('Failed to copy:', { type });
      return;
    }

    if (type === 'email') {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } else {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="detail-container">
        <div className="loading">Loading review...</div>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="detail-container">
        <div className="error-message">{error || 'Review not found'}</div>
        <button onClick={() => navigate('/dashboard?tab=reviews')} className="btn btn-primary">
          Back to Reviews
        </button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader title="Review Details" showNav={false} showEmailButton={false} />
      <div className="dashboard-content">
        <div className="detail-container">
          <div className="detail-header">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              ← Back to Reviews
            </button>
          </div>

      <div className="review-detail-card">
        <div className="review-header-section">
          <div className="reviewer-info">
            <h2>{review.name}</h2>
            <div className="contact-info-row">
              <span className="review-email">{review.email}</span>
              <button
                onClick={() => copyToClipboard(review.email, 'email')}
                className="copy-button"
                title="Copy email"
              >
                {copiedEmail ? '✓ Copied' : '📋'}
              </button>
            </div>
            <div className="contact-info-row">
              <span className="review-phone">{formatPhoneNumber(review.phone)}</span>
              <button
                onClick={() => copyToClipboard(review.phone, 'phone')}
                className="copy-button"
                title="Copy phone"
              >
                {copiedPhone ? '✓ Copied' : '📋'}
              </button>
            </div>
          </div>
          <div className="review-rating-section">
            <div className="rating-display">
              <span className="rating-number">{review.rating}/10</span>
            </div>
          </div>
        </div>

        <div className="review-body-section">
          <h3>Comment</h3>
          <p className="review-comment">{review.comment}</p>
        </div>

        {review.questions && review.questions.length > 0 && (
          <div className="review-ratings-section">
            <h3>Review Questions</h3>
            <div className="ratings-grid">
              {review.questions.map((q) => (
                <div key={q.id} className="rating-item">
                  <span className="rating-label">{q.question_title}:</span>
                  <span className="rating-value">{q.pivot.rating}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="review-meta-section">
          <div className="meta-item">
            <span className="meta-label">Created:</span>
            <span className="meta-value">{new Date(review.created_at).toLocaleString()}</span>
          </div>
          <div className="meta-item">
            <span className="meta-label">Updated:</span>
            <span className="meta-value">{new Date(review.updated_at).toLocaleString()}</span>
          </div>
          {review.client && (
            <div className="meta-item">
              <span className="meta-label">Company:</span>
              <span className="meta-value">{review.client.client_name}</span>
            </div>
          )}
          {review.ip && (
            <div className="meta-item">
              <span className="meta-label">IP Address:</span>
              <span className="meta-value">{review.ip}</span>
            </div>
          )}
          <div className="meta-item">
            <span className="meta-label">Status:</span>
            <span className={`meta-value status-badge ${review.status === 'approved' ? 'status-approved' : 'status-pending'}`}>
              {review.status || 'pending'}
            </span>
          </div>
        </div>

        {/* Action Buttons Section */}
        <div className="review-actions-section">
          {!isAdmin() && (
            <>
              <div className="action-group">
                <label className="show-on-site-checkbox">
                  <input
                    type="checkbox"
                    checked={showOnSite}
                    onChange={handleShowOnSiteToggle}
                    disabled={updating}
                  />
                  <span>Show on Site</span>
                </label>
              </div>
              {review.status !== 'approved' && (
                <button
                  onClick={handleApprove}
                  disabled={updating}
                  className="btn-approve-review"
                >
                  {updating ? 'Approving...' : 'Approve Review'}
                </button>
              )}
            </>
          )}
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="btn-delete-review"
          >
            {deleting ? 'Deleting...' : 'Delete Review'}
          </button>
        </div>
      </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ReviewDetail;
